import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Grid, Container } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BreadCrumb, Button, Modal, Typography } from "design_system/src";
import SelectorGroup from "design_system/src/components/SelectorGroup/SelectorGroup";
import { ISkuVariant, TSku } from "models/inventory/interfaces/sku.interface";
import { colorOptions } from "models/inventory/interfaces/vehicle.interface";
import { getSkuByCode } from "models/inventory/controllers/sku.controller";
import MetaTags from "components/head/MetaTags";
import { NotFoundScreen } from "views/NotFoundScreen";
import { getVehicleDetailsBySku } from "models/inventory/controllers/vehicle-detail.controller";
import { getVehicleReviews } from "models/inventory/controllers/vehicle-reviews.controller";
import { isVehicleDetailComplete, vehicleDetailData } from "models/inventory/interfaces/vehicle-detail.interfaces";
import { VehicleReviewSummary } from "models/inventory/interfaces/vehicle-review-summary.interfaces";
import { formatPrice } from "helpers/formatPrice";
import { prices } from "helpers/prices";
import { LoadingScreen } from "views/LoadingScreen";
import { useFinancialForm } from "hooks/useFinancialForm";
import LoanCalculator from "components/loanCalculator/LoanCalculator";
import useAlliance from "hooks/useAlliance";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { clearApplicationPartnership, clearApplicationVehicle, setApplicationSku, setApplicationVariant } from "store/actions/application";
import { useSelectedDownPayment, useSelectedLoanDuration } from "hooks/useVehicleSelection";
import {
  fetchUpdateFinancialForm,
  fetchUpdateCreditLineFinancialForm,
} from "models/financialForm/controllers/financialForm.controller";
import { isCreditLineFinancialForm } from "models/financialForm/interfaces/financialForm.interface";
import { toasterNotification, ToasterOptions } from "helpers/toastNotification";
import { WhatsappButton } from "views/miCuenta/components/whatsappButton/WhatsappButton";
import Description from "./components/Description/Description";
import RealState from "./components/Description/RealState";
import DetailTags from "./components/DetailTags/DetailTags";
import ReviewsSummary from "./components/Reviews/ReviewsSummary";
import Specs from "./components/Specs/Specs";
import Photos from "./components/Photos/Photos";
import Reviews from "./components/Reviews/Reviews";
import OutdoorPhotos from "./components/OutdoorPhotos/OutdoorPhotos";
import FeaturedPhotos from "./components/FeaturedPhotos/FeaturedPhotos";


const VehicleDetail = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id: code } = useParams();
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const { financialForm } = useFinancialForm();
  const { allianceUrl } = useAlliance();
  const selectedLoanDuration = useSelectedLoanDuration();
  const selectedDownPayment = useSelectedDownPayment();
  const tempToken = useSelector((state: RootState) => state.userReducer.tempToken);
  const accessToken = useSelector((state: RootState) => state.userReducer.accessToken);
  const isTempTokenSession = !!tempToken && !accessToken;
  

  const [sku, setSku] = useState<TSku>();
  const [loading, setLoading] = useState<boolean>(true);
  const [nonExistentSku, setNonExistentSku] = useState<boolean>(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string>();

  const [vehicleDetails, setVehicleDetails] = useState<vehicleDetailData>();
  const [vehicleReview, setVehicleReview] = useState<VehicleReviewSummary>();
  const [showConfirmChangesModal, setShowConfirmChangesModal] = useState(false);
  const [showBlockedChangeModal, setShowBlockedChangeModal] = useState(false);

  const skuVariant = useMemo<ISkuVariant | undefined>(() => {
    if (!sku || !selectedVariantId) return undefined;
    return sku.variants?.find((item) => item._id === selectedVariantId);
  }, [selectedVariantId]);

  const showOutdoorPhotos = useMemo<boolean>(
    () => !!vehicleDetails?.images?.length,
    [vehicleDetails],
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 600px)");

    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mediaQuery.matches);

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    (async () => {
      setSku(undefined);

      if(!code) {
        setLoading(false);
        return;
      }

      const res = await getSkuByCode(code);
      if (res) {
        setSku(res);
      } else {
        setNonExistentSku(true);
      }
      setLoading(false);
    })();
  }, [code]);

  useEffect(() => {
    if (!sku) return;

    const variants = sku.variants ?? [];
    const savedVariant = financialForm?.sku === sku._id
      ? variants.find((variant) => variant._id === financialForm?.skuVariant)
      : undefined;

    setSelectedVariantId(savedVariant?._id ?? (variants.length > 0 ? variants[0]._id : undefined));
  }, [sku, financialForm?.sku, financialForm?.skuVariant]);

  useEffect(() => {
    if (!sku) return;

    getVehicleDetailsBySku(sku._id).then((data) => {
      setVehicleDetails(isVehicleDetailComplete(data) ? data : undefined);
    });
    getVehicleReviews({
      brand: { _id: sku.brand },
      model: { _id: sku.model },
      cylindersCapacity: sku.cc,
      year: sku.year,
    }).then((data) => {
      setVehicleReview(data as VehicleReviewSummary);
    });
  }, [sku?._id]);

  const variants = sku?.variants ?? [];
  const selectedVariant = variants.find((v) => v._id === selectedVariantId);
  const hasMultipleVariants = variants.length > 1;
  const displayPrice = selectedVariant?.price ?? sku?.minPrice ?? 0;

  const variantOptions = useMemo(
    () => variants.map((variant): { id: string; text: string } => {
      const colorLabel = variant.color
        ? colorOptions.find((c) => c.value === variant.color)?.label ?? variant.color
        : null;
      const parts = [colorLabel ? `Color: ${colorLabel}` : null, variant.hasGps ? "Con GPS" : null].filter(Boolean);
      const description = parts.length > 0 ? parts.join(" · ") : "Estándar";
      return {
        id: variant._id,
        text: description,
      };
    }),
    [variants]
  );

  const updateFinancialFormApplication = async (financialFormId: string) => {
    if (isCreditLineFinancialForm(financialForm)) {
      const payload = {
        ...(selectedVariant ? { skuVariant: selectedVariant._id } : {}),
        ...(sku ? { sku: sku._id } : {}),
        ...(selectedLoanDuration ? { loanDuration: selectedLoanDuration } : {}),
        ...(selectedDownPayment ? { downPayment: selectedDownPayment } : {}),
      };

      await fetchUpdateCreditLineFinancialForm(financialFormId, payload);
      return;
    }

    const payload: {
      sku?: string;
      skuVariant?: string;
      loanApplication?: {
        downPayment?: number;
        loanDuration?: number;
      }
    } = {
      ...(selectedVariant ? { skuVariant: selectedVariant._id } : {}),
      ...(sku ? { sku: sku._id } : {}),
    };

    if (selectedLoanDuration || selectedDownPayment) payload.loanApplication = {
      ...(selectedLoanDuration ? { loanDuration: selectedLoanDuration } : {}),
      ...(selectedDownPayment ? { downPayment: selectedDownPayment } : {}),
    };

    await fetchUpdateFinancialForm(financialFormId, payload);
  };

  const proceedWithContinue = async () => {
    dispatch(clearApplicationVehicle());
    dispatch(clearApplicationPartnership());

    if (sku) {
      dispatch(setApplicationSku(sku));
      if (selectedVariant) {
        dispatch(setApplicationVariant(selectedVariant));
      }
    }

    const hasActiveSession = !!accessToken || !!tempToken;

    if (financialForm && sku && hasActiveSession) {
      try {
        await updateFinancialFormApplication(financialForm._id);
      } catch (err: any) {
        if (err?.statusCode === 403) {
          setShowBlockedChangeModal(true);
        } else {
          toasterNotification({
            msg: err?.message || "No se pudo actualizar la moto seleccionada",
            toasterType: ToasterOptions.error,
            style: "light",
          });
        }
        return;
      }
      navigate(
        isTempTokenSession && !isCreditLineFinancialForm(financialForm)
          ? `/financia-tu-moto/pre-oferta/${financialForm._id}`
          : "/mi-cuenta"
      );
    } else if (sku) {
      navigate(allianceUrl ? `/${allianceUrl}/financia-tu-moto` : "/financia-tu-moto");
    }
  };

  const onContinue = async () => {
    // Ya existe una solicitud con moto/plan guardados: confirmar antes de
    // sobreescribirlos (podrían estar ligados a un enganche en proceso).
    if (financialForm && sku) {
      setShowConfirmChangesModal(true);
      return;
    }
    await proceedWithContinue();
  };

  const handleConfirmChanges = async () => {
    setShowConfirmChangesModal(false);
    await proceedWithContinue();
  };

  const continueDisabled = !(financialForm && sku) && hasMultipleVariants && !selectedVariant;

  if (loading || isMobile === null) return <LoadingScreen />;

  return (
    <>
      {nonExistentSku && <MetaTags
        title="Compra y vende tu moto nueva o seminueva a crédito en CDMX"
        description="Financia tu moto nueva o seminueva en CDMX. Compra y venta fácil, pagos cómodos, entrega inmediata en Ozon."
        noIndex
      />}
      {!nonExistentSku && <MetaTags
        title={`Compra tu ${sku?.brandName} ${sku?.modelName} ${sku?.cc}cc año ${sku?.year} en Ozon`}
        description={`Financia tu moto ${sku?.brandName} ${sku?.modelName} de ${sku?.cc}cc, año ${sku?.year}. Entrega rápida y pagos cómodos. `}
      />}
      {nonExistentSku && <NotFoundScreen/>}
      {!nonExistentSku &&
        <Container maxWidth="xl" className="p_t_lg">
          <Grid
            container
            justifyContent="center"
            columnSpacing={{ xs: 0, sm: 2, md: 3, lg: 3, xl: 3 }}
            rowSpacing={2}
            className="m_b_xl p_x_xxxl_desktop"
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
            >
              <BreadCrumb url={location.pathname} />
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={7}
              className="m_b_md"
            >
              <Photos
                photos={sku?.images ?? []}
                isMobile={isMobile}
              />

              {/* Description section for desktop devices */}
              {!isMobile && sku && <Description sku={sku} vehicleDetails={vehicleDetails}/>}
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={5}
              className="m_b_md"
            >
              <div className="dso_card p_x_xl p_y_lg m_b_xl_mobile">
                <div className="m_b_sm">
                  <DetailTags sku={sku} skuVariant={skuVariant} />
                </div>

                <Typography
                  scale="heading1"
                  scaleMobile="heading2"
                  weight="600"
                  component="h1"
                  className="m_none"
                >
                  {sku?.brandName ?? ""} {sku?.modelName ?? ""} {sku?.cc ?? ""}
                </Typography>

                {vehicleReview && vehicleReview?.count > 0 && <ReviewsSummary value={vehicleReview?.getGlobalRating()} amount={vehicleReview?.count}/>}

                <Typography
                  scale="large"
                  scaleMobile="large"
                  weight="400"
                  component="p"
                  className="m_none p_y_xs m_b_md"
                >
                  Precio: <Typography
                    scale="large"
                    scaleMobile="large"
                    weight="600"
                    component="span"
                    textColor="primary_300"
                  >
                    ${formatPrice(prices(displayPrice), "")}
                  </Typography>
                </Typography>

                {hasMultipleVariants && (
                  <div className="m_b_md">
                    <SelectorGroup
                      multiple={false}
                      value={selectedVariantId ? [selectedVariantId] : []}
                      options={variantOptions}
                      orientation="horizontal"
                      className="flex_gap_sm"
                      onChange={(selected) => setSelectedVariantId(selected[0] as string)}
                    />
                  </div>
                )}

                {sku && (
                  <LoanCalculator sku={sku} price={displayPrice} onContinueBtn={onContinue} btnDisabled={continueDisabled} />
                )}
              </div>

              {/* Description section for mobile devices */}
              {isMobile && sku &&<Description sku={sku} vehicleDetails={vehicleDetails}/>}
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={showOutdoorPhotos ? 6 : 12}
              className="m_b_md"
            >
              <Specs sku={sku} vehicleDetails={vehicleDetails} fullWidth={showOutdoorPhotos} />
            </Grid>

            {!isMobile && showOutdoorPhotos && <Grid
              item
              xs={12}
              sm={12}
              md={6}
              className="m_b_md"
            >
              <OutdoorPhotos sku={sku} vehicleDetails={vehicleDetails} />
            </Grid>}

            <Grid
              item
              xs={12}
              className="m_b_md"
            >
              <FeaturedPhotos
                sku={sku}
                vehicleDetails={vehicleDetails}
                isMobile={isMobile}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              className="m_b_md"
            >
              <RealState vehicle={undefined} />
              <Reviews sku={sku} vehicleReview={vehicleReview} />
            </Grid>
          </Grid>
        </Container>
      }

      <Modal open={showConfirmChangesModal} setOpen={setShowConfirmChangesModal}>
        <Typography scale="large" weight="400" className="m_b_sm text_center">
          ¿Deseas continuar con los cambios?
        </Typography>
        <Typography scale="small" weight="400" className="m_b_xl text_center">
          Vas a actualizar la moto y/o el plan de tu solicitud.
        </Typography>
        <div className="display_flex flex_gap_md">
          <Button
            className="w_100_per"
            scale="small"
            variant="outline"
            onClick={() => setShowConfirmChangesModal(false)}
          >
            <Typography weight="600" scale="medium">
              Cancelar
            </Typography>
          </Button>
          <Button
            className="w_100_per"
            scale="small"
            onClick={handleConfirmChanges}
          >
            <Typography weight="600" scale="medium">
              Continuar
            </Typography>
          </Button>
        </div>
      </Modal>

      {showBlockedChangeModal && createPortal(
        <div className="modalcontainer open">
          <div className="backdrop" />
          <div className="dso_card bg_neutral_0 w_90_vp_mobile modal p_xxxl display_flex">
            <div className="flex_center_col w_100_per">
              <Typography scale="large" weight="400" className="m_b_sm text_center">
                No puedes modificar la moto o el plan de esta solicitud
              </Typography>
              <Typography scale="small" weight="400" className="m_b_xl text_center">
                Tu enganche ya está pagado o en proceso de pago. Para realizar cambios en las condiciones de tu solicitud, comunícate con tu asesor.
              </Typography>
              <WhatsappButton />
              <Button
                className="w_100_per m_t_md"
                scale="small"
                variant="outline"
                onClick={() => navigate("/mi-cuenta")}
              >
                <Typography weight="600" scale="medium">
                  Ir a mi cuenta
                </Typography>
              </Button>
            </div>
          </div>
        </div>,
        document.getElementById("modal-root") as HTMLElement
      )}
    </>
  );
};

export default VehicleDetail;
